import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const navLinks = [
  { label: 'サービス概要', id: 'service' },
  { label: '選ばれる理由', id: 'benefits' },
  { label: '比較表', id: 'comparison' },
  { label: '料金', id: 'pricing' },
  { label: 'サポート', id: 'support' },
  { label: 'FAQ', id: 'faq' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToContact = () => {
    setMobileOpen(false);
    navigate('/contact');
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16 lg:h-[72px]">
          {/* Logo */}
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2.5 cursor-pointer"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/4944a4ba-c62a-4964-bf85-4ed6714cc305.png"
              alt="Trim-Page Logo"
              className="w-8 h-8 object-contain"
            />
            <span
              className={`text-xl font-bold transition-colors duration-300 ${
                scrolled ? 'text-[#1a2332]' : 'text-[#1a2332]'
              }`}
            >
              Trim-Page
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`px-3.5 py-2 text-sm font-medium rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'text-[#4a5568] hover:text-[#1a2332] hover:bg-[#f5f3f0]'
                    : 'text-[#4a5568] hover:text-[#1a2332] hover:bg-black/5'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <button
            onClick={goToContact}
            className="hidden lg:block px-6 py-2.5 bg-[#1a2332] text-white text-sm font-medium rounded-full hover:bg-[#2d3748] transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            無料相談
          </button>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer relative z-50"
            aria-label="メニューを開く"
          >
            <div className="w-6 flex flex-col gap-1.5">
              <span
                className={`block h-[2px] rounded-full transition-all duration-300 ${
                  mobileOpen
                    ? 'bg-[#1a2332] rotate-45 translate-y-[5px]'
                    : scrolled
                    ? 'bg-[#1a2332]'
                    : 'bg-[#1a2332]'
                }`}
              />
              <span
                className={`block h-[2px] rounded-full transition-all duration-300 ${
                  mobileOpen
                    ? 'opacity-0'
                    : scrolled
                    ? 'bg-[#1a2332]'
                    : 'bg-[#1a2332]'
                }`}
              />
              <span
                className={`block h-[2px] rounded-full transition-all duration-300 ${
                  mobileOpen
                    ? 'bg-[#1a2332] -rotate-45 -translate-y-[5px]'
                    : scrolled
                    ? 'bg-[#1a2332]'
                    : 'bg-[#1a2332]'
                }`}
              />
            </div>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 z-40 w-[280px] h-full bg-white shadow-2xl transition-transform duration-300 ease-in-out lg:hidden ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="pt-20 px-6 pb-8 flex flex-col h-full">
          <nav className="flex flex-col gap-1 flex-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="w-full text-left px-4 py-3.5 text-[#2d3748] font-medium text-base rounded-lg hover:bg-[#f5f3f0] transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </button>
            ))}
          </nav>
          <button
            onClick={goToContact}
            className="w-full py-3.5 bg-[#1a2332] text-white font-medium rounded-full hover:bg-[#2d3748] transition-colors cursor-pointer whitespace-nowrap mt-4"
          >
            無料相談を申し込む
          </button>
        </div>
      </div>
    </>
  );
}
